import {
  EventType,
  InteractionType,
  ProtocolMode,
  PublicClientApplication,
  BrowserCacheLocation,
  InteractionRequiredAuthError
} from "@azure/msal-browser";

import type {
  Configuration,
  RedirectRequest,
  AuthenticationResult,
  EventMessage,
  AccountInfo
} from "@azure/msal-browser";

import { bugsnagClient } from "./bugsnag";

const scopes = [
  "roles",
  "openid",
  "profile",
  "offline_access",
  `wib-api-${process.env.WIB_WORKSPACE_ID}`
];

export const redirectRequest: RedirectRequest = {
  scopes,
  redirectUri: `${window.location.origin}/auth/login`
};

export const msalConfig: Configuration = {
  auth: {
    clientId: `wib-js-${process.env.WIB_WORKSPACE_ID}`,
    authority: process.env.WIB_AUTH_URL,
    knownAuthorities: [process.env.WIB_AUTH_URL],
    postLogoutRedirectUri: process.env.WIB_PORTAL_URL
  },
  system: {
    // msal-browser v5 moved protocolMode from auth to system.
    protocolMode: ProtocolMode.OIDC
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage
  }
};

export const msalInstance = new PublicClientApplication(msalConfig);

// One-time setup for the MSAL singleton. Await this once at app startup, before
// rendering, so the active account and event subscriptions are wired up before
// any component or fetch tries to read them.
//
// msal-browser v3+ requires initialize() to resolve before any other API is
// called (getAllAccounts included). MsalProvider also calls initialize(), but
// it's idempotent, so awaiting it here as well is safe.
export async function initializeMsal() {
  await msalInstance.initialize();

  // pick up an existing account on first load (e.g., after a refresh)
  const existingAccounts = msalInstance.getAllAccounts();

  if (existingAccounts.length > 0) {
    msalInstance.setActiveAccount(existingAccounts[0]);
  }

  // Cross-tab account storage events are always enabled in v5; the old
  // enableAccountStorageEvents() opt-in no longer exists.

  // keep the active account in sync with login/logout events. Registered once
  // at startup so we don't accumulate duplicate callbacks across re-renders.
  msalInstance.addEventCallback((event: EventMessage) => {
    // v5: LOGIN_SUCCESS carries an AccountInfo payload directly, while
    // ACQUIRE_TOKEN_SUCCESS still carries an AuthenticationResult.
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      msalInstance.setActiveAccount(event.payload as AccountInfo);
      return;
    }

    if (
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
      (event.payload as AuthenticationResult)?.account
    ) {
      msalInstance.setActiveAccount(
        (event.payload as AuthenticationResult).account
      );
      return;
    }

    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      msalInstance.setActiveAccount(null);
      return;
    }

    // v5 folded LOGIN_FAILURE into ACQUIRE_TOKEN_FAILURE. We only report the
    // Redirect-interaction failures (the old LOGIN_FAILURE cases: a failed
    // redirect response or a loginRedirect/acquireTokenRedirect that couldn't
    // start). Silent failures are deliberately ignored here: they fire for
    // every recoverable InteractionRequiredAuthError that
    // getActiveAccountAccessToken already handles by falling back to
    // acquireTokenRedirect, so reporting them would flood Bugsnag with
    // phantom failures. Genuine unrecoverable silent-token errors are reported
    // from getActiveAccountAccessToken's catch block below instead.
    if (
      event.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
      event.interactionType === InteractionType.Redirect
    ) {
      bugsnagClient.notify(
        event.error ?? new Error("MSAL redirect ACQUIRE_TOKEN_FAILURE")
      );
    }
  });
}

// dedupe concurrent interactive calls. Multiple fetches firing on initial page
// load were each calling loginRedirect, and the second-and-later calls were
// throwing BrowserAuthError: interaction_in_progress.
let interactiveInFlight: Promise<void> | null = null;

function startInteractive(fn: () => Promise<void>): Promise<void> {
  if (!interactiveInFlight) {
    interactiveInFlight = fn().finally(() => {
      interactiveInFlight = null;
    });
  }
  return interactiveInFlight;
}

export async function getActiveAccountAccessToken() {
  const activeAccount = msalInstance.getActiveAccount();

  if (!activeAccount) {
    await startInteractive(() => msalInstance.loginRedirect(redirectRequest));
    return;
  }

  try {
    const accessToken = await msalInstance.acquireTokenSilent({
      account: activeAccount,
      scopes
    });

    if (!accessToken) {
      throw new Error("no token received");
    }

    return accessToken;
  } catch (error) {
    // if silent fails, fall back to redirect
    if (error instanceof InteractionRequiredAuthError) {
      await startInteractive(() =>
        msalInstance.acquireTokenRedirect({
          account: activeAccount,
          scopes
        })
      );
      return;
    }
    // additional "non-interaction" errors — token acquisition has genuinely
    // failed with no recovery path. Log to Bugsnag so we can see it server-
    // side, then re-throw so the calling component (SWR, fetch caller, etc.)
    // can surface it in its own error state.
    bugsnagClient.notify(
      error instanceof Error ? error : new Error(String(error))
    );
    throw error;
  }
}
